import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { touchpointKeys, touchpointVersionKeys } from "hooks/api/query-keys";
import { useCampaignJobStates, useTouchpoint } from "hooks/api/use-touchpoints";

import { getCampaignPaths, getTouchpointPaths } from "utilities/app-routes";
import {
  clearEmailWriterPollingSession,
  getEmailWriterPollingSession,
} from "utilities/email-writer-polling-session";
import { getEmailWriterJobsOutcome } from "utilities/email-writer-completion";
import { showEmailWriterCompletionToast } from "utilities/show-email-writer-completion-toast";

const POLL_INTERVAL_MS = 3000;

type UseEmailWriterCompletionPollingParams = {
  clientId: string;
  campaignId: string;
  campaignName: string;
  enabled: boolean;
};

const isOnPath = (pathname: string, targetPath: string) =>
  pathname === targetPath || pathname.startsWith(`${targetPath}/`);

export const useEmailWriterCompletionPolling = ({
  clientId,
  campaignId,
  campaignName,
  enabled,
}: UseEmailWriterCompletionPollingParams) => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const session = getEmailWriterPollingSession(campaignId);
  const rewrittenTouchpointId =
    session?.touchpointIds?.length === 1 ? session.touchpointIds[0] : undefined;

  const { data: jobStates, dataUpdatedAt } = useCampaignJobStates(
    { enabled: enabled && !!session, refetchInterval: POLL_INTERVAL_MS, refetchOnWindowFocus: false },
    { clientId, campaignId },
  );
  const { data: rewrittenTouchpoint } = useTouchpoint(
    rewrittenTouchpointId ?? "",
    { enabled: enabled && !!rewrittenTouchpointId },
    { clientId, campaignId },
  );
  const rewrittenTouchpointName = rewrittenTouchpoint?.name;
  const sessionStartedAt = session?.startedAt;
  const acceptJobStatesAfterRef = useRef(Number.POSITIVE_INFINITY);

  // The job-states query is shared with useCampaignJobPolling, so a fetch sent before the job was
  // queued can still be in flight and would report the previous run as finished. Invalidating
  // cancels it, so only data landing after this point comes from a request sent after the queueing.
  useEffect(() => {
    if (sessionStartedAt === undefined) {
      return;
    }

    const jobStatesKey = touchpointKeys.jobStates(clientId, campaignId);

    acceptJobStatesAfterRef.current = Date.now();
    queryClient.cancelQueries({ queryKey: jobStatesKey });
    queryClient.invalidateQueries({ queryKey: jobStatesKey });
  }, [campaignId, clientId, queryClient, sessionStartedAt]);

  useEffect(() => {
    const currentSession = getEmailWriterPollingSession(campaignId);

    if (!enabled || !currentSession || !jobStates || dataUpdatedAt <= acceptJobStatesAfterRef.current) {
      return;
    }

    const outcome = getEmailWriterJobsOutcome(jobStates.states, currentSession.touchpointIds);

    if (outcome === "running") {
      return;
    }

    clearEmailWriterPollingSession(campaignId);

    if (outcome !== "succeeded") {
      return;
    }

    queryClient.invalidateQueries({ queryKey: touchpointKeys.lists() });
    queryClient.invalidateQueries({ queryKey: touchpointKeys.details() });
    queryClient.invalidateQueries({ queryKey: touchpointVersionKeys.all });

    const singleTouchpointId =
      currentSession.touchpointIds?.length === 1 ? currentSession.touchpointIds[0] : undefined;

    if (singleTouchpointId) {
      const { touchpointPreviewPath } = getTouchpointPaths({
        clientId,
        campaignId,
        touchpointId: singleTouchpointId,
      });

      showEmailWriterCompletionToast(
        isOnPath(pathname, touchpointPreviewPath)
          ? { title: "Email written", autoClose: true }
          : {
              title: "Email written",
              message: `Drafted email content is now available to review for ‘${rewrittenTouchpointName ?? "email"}’.`,
              actionLabel: "View preview",
              destinationPath: touchpointPreviewPath,
              onNavigate: navigate,
            },
      );
      return;
    }

    const { campaignCreativePath } = getCampaignPaths({ clientId, campaignId });

    showEmailWriterCompletionToast({
      title: "Writing complete",
      message: `Drafted content is now available to review for ‘${campaignName}’ campaign.`,
      ...(!isOnPath(pathname, campaignCreativePath) && {
        actionLabel: "View in Creative",
        destinationPath: campaignCreativePath,
        onNavigate: navigate,
      }),
    });
  }, [
    campaignId,
    campaignName,
    clientId,
    dataUpdatedAt,
    enabled,
    jobStates,
    navigate,
    pathname,
    queryClient,
    rewrittenTouchpointName,
  ]);
};
